import { get, set } from "lodash-es";
import { isRef, Reactive, reactive, toRaw } from "vue";

/**
 * 객체의 모든 속성을 선택적으로 만드는 타입
 * 중첩된 객체도 재귀적으로 처리
 */
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * 속성 변경 시 호출될 콜백 함수 타입
 */
type SetterCallback = (prop: string, value: any) => void;

export class SyncManager {
  /**
   * 프록시 객체를 캐싱하기 위한 WeakMap
   * 메모리 누수를 방지하기 위해 WeakMap 사용
   */
  private static proxyCache = new WeakMap<object, any>();

  /**
   * 객체를 반응형으로 만들고 프록시를 생성하는 메인 메서드
   * @param target 동기화할 대상 객체
   * @param values 초기값 (선택적)
   * @param onSet 속성 변경 시 호출될 콜백 (선택적)
   * @returns 프록시된 반응형 객체
   */
  static sync<T extends object>(target: T, values: DeepPartial<T> = {}, onSet?: SetterCallback): T {
    const baseTarget = reactive(toRaw(target)) as Reactive<T>;
    const proxy = this.getOrCreateProxy(baseTarget, onSet);

    // 초기값 설정을 비동기로 처리하여 성능 최적화
    queueMicrotask(() => {
      this.updateValues(proxy, values);
    });

    return proxy as T;
  }

  /**
   * 캐시된 프록시를 반환하거나 새로운 프록시를 생성
   * @param target 프록시할 대상 객체
   * @param onSet 속성 변경 콜백
   * @returns 캐시된 프록시 또는 새로운 프록시
   */
  private static getOrCreateProxy<T extends object>(target: T, onSet?: SetterCallback): T {
    if (this.proxyCache.has(target)) {
      return this.proxyCache.get(target);
    }

    const proxy = this.createProxy(target, onSet);
    this.proxyCache.set(target, proxy);
    return proxy;
  }

  /**
   * 새로운 프록시 객체 생성
   * @param target 프록시할 대상 객체
   * @param onSet 속성 변경 콜백
   * @returns 프록시된 객체
   */
  private static createProxy<T extends object>(target: T, onSet?: SetterCallback): T {
    const handler = this.createProxyHandler(onSet);
    return new Proxy(target, handler);
  }

  /**
   * 프록시 핸들러 객체 생성
   * get과 set 트랩을 포함
   * @param onSet 속성 변경 콜백
   * @returns 프록시 핸들러 객체
   */
  private static createProxyHandler(onSet?: SetterCallback): ProxyHandler<any> {
    return {
      get: (obj, prop: string) => {
        const value = get(obj, prop);

        if (isRef(value)) {
          return value.value;
        }

        if (Array.isArray(value)) {
          // 'obj'는 부모 프록시(또는 반응형 객체), 'prop'은 이 배열의 속성 이름입니다.
          // 'value'는 실제 배열입니다.
          return new Proxy(value, {
            get: (arrTarget, arrPropKey: any) => {
              const methodOrValue = arrTarget[arrPropKey];
              if (typeof methodOrValue === "function") {
                // 배열 메서드 (push, pop, splice 등) 호출 가로채기
                return (...args: any[]) => {
                  const result = methodOrValue.apply(arrTarget, args);
                  // onSet 콜백 호출: prop은 배열의 이름, arrTarget은 변경된 배열
                  onSet?.(prop as string, arrTarget);
                  return result;
                };
              }
              return methodOrValue;
            },
            set: (arrTarget, arrIndexOrLength, newValueInArray) => {
              // 배열 요소 직접 할당 (예: arr[0] = 1) 또는 length 변경 가로채기
              const success = Reflect.set(arrTarget, arrIndexOrLength, newValueInArray);
              if (success) {
                // onSet 콜백 호출: prop은 배열의 이름, arrTarget은 변경된 배열
                onSet?.(prop as string, arrTarget);
              }
              return success;
            },
            deleteProperty: (arrTarget, arrPropKey) => {
              // 배열 요소 삭제 (예: delete arr[0]) 가로채기
              const success = Reflect.deleteProperty(arrTarget, arrPropKey);
              if (success) {
                // onSet 콜백 호출: prop은 배열의 이름, arrTarget은 변경된 배열
                onSet?.(prop as string, arrTarget);
              }
              return success;
            },
          });
        }

        if (this.shouldCreateNestedProxy(value)) {
          return this.getOrCreateProxy(value, onSet);
        }

        return value;
      },

      set: (obj, prop: string, value) => {
        const currentValue = get(obj, prop);
        if (this.hasValueChanged(currentValue, value)) {
          this.setValue(obj, prop, value);
          onSet?.(prop, value);
        }
        return true;
      },
    };
  }

  /**
   * 객체의 속성 값을 설정
   * setter 메서드가 있으면 사용하고, 없으면 직접 설정
   * @param obj 대상 객체
   * @param prop 속성 이름
   * @param value 설정할 값
   */
  private static setValue(obj: any, prop: string, value: any): void {
    const setterName = `set${prop.charAt(0).toUpperCase() + prop.slice(1)}`;

    if (typeof obj[setterName] === "function") {
      obj[setterName](value);
    } else {
      set(obj, prop, value);
    }
  }

  /**
   * 객체의 값들을 재귀적으로 업데이트
   * @param target 업데이트할 대상 객체
   * @param values 업데이트할 값들
   */
  private static updateValues<T extends object>(target: Reactive<T>, values: DeepPartial<T>): void {
    Object.entries(values).forEach(([key, value]) => {
      if (this.isNestedObject(value)) {
        const currentValue = get(target, key);
        if (this.isNestedObject(currentValue)) {
          this.updateValues(currentValue as Reactive<T[keyof T]>, value as DeepPartial<T[keyof T]>);
        } else {
          this.setValue(target, key, reactive(value as any));
        }
      } else {
        this.setValue(target, key, value);
      }
    });
  }

  /**
   * 값이 중첩 프록시를 생성해야 하는지 확인
   * @param value 검사할 값
   * @returns 중첩 프록시 생성 필요 여부
   */
  private static shouldCreateNestedProxy(value: any): boolean {
    return typeof value === "object" && value !== null && !isRef(value);
  }

  /**
   * 값이 중첩된 객체인지 확인
   * @param value 검사할 값
   * @returns 중첩된 객체 여부
   */
  private static isNestedObject(value: any): boolean {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  /**
   * 두 값이 실제로 다른지 확인
   * @param oldValue 이전 값
   * @param newValue 새로운 값
   * @returns 값 변경 여부
   */
  private static hasValueChanged(oldValue: any, newValue: any): boolean {
    return !Object.is(toRaw(oldValue), toRaw(newValue));
  }
}
